import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { CompassIcon } from '@atlaskit/logo';
import Tag from '@atlaskit/tag/removable-tag';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResulType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import {
	SearchMetadata,
	type SearchMetadataPart,
} from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultCompassStatusLozenge } from '../../../../../../common/ui/search-result-compass-status-lozenge';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { truncateTextWithQuery } from '../../../../../../common/utils/truncate-text-with-query';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultAnalyticsCallbacks } from '../../../../../../controllers/analytics/result-analytics-callbacks';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchCompassComponent_AtlassianSearchPage$key } from './__generated__/searchCompassComponent_AtlassianSearchPage.graphql';
import { CompassOwnerName } from './compass-owner-name';
import { messages } from './messages';

export type SearchCompassComponentProps =
	SearchResultProps<searchCompassComponent_AtlassianSearchPage$key> & {
		compassTeamName?: string | null;
	};

export const SearchCompassComponent = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	searchQuery,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	cloudId,
	compassTeamName,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchCompassComponentProps & ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchCompassComponent_AtlassianSearchPage on SearchResultCompassComponent {
				__typename
				id
				title
				description
				type
				url
				lastModifiedDate
				tier
				component {
					description
					state
				}
			}
		`,
		node,
	);

	const { id, title, tier, component, lastModifiedDate, type, description } = data;
	const { state, description: componentDescription } = component || {};

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(id);
		return {
			id,
			containerId: null,
			spaceId: null,
			contentType: type,
			entityType: type,
			contentId,
			product,
			...getSiteMetadata(id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [id, type, cloudId, selectedSites]);

	const url = useMemo(() => {
		return getGenericUrlWithAnalyticsQueryParams({ url: data.url, searchSessionId });
	}, [data.url, searchSessionId]);

	const enrichedTitle = useMemo(() => {
		return enrichExcerpt(title);
	}, [title]);

	const enrichedDescription = useMemo(() => {
		if (description) {
			return processExcerpt(description);
		}
		const reducedDescription = truncateTextWithQuery(componentDescription, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [componentDescription, description, searchQuery]);

	const titleMetadataParts: SearchMetadataPart[] = useMemo(
		() => [
			['componentState', <SearchResultCompassStatusLozenge state={state} />],
			['tier', tier ? <Tier tier={tier} /> : null],
		],
		[state, tier],
	);

	const headerMetadataParts = useMemo(
		() => [
			['type', <SearchResultType type={type as SearchClientSearchResulType} />],
			[
				'ownerName',
				compassTeamName || compassTeamName === undefined ? (
					// we need to pass data.ownerName here rather than unconditionally rendering this component
					// as sometimes there is no owner to render, which results in `• •` showing up in the metadata
					<CompassOwnerName
						displayName={compassTeamName}
						isLoading={compassTeamName === undefined}
					/>
				) : null,
			],
			['lastModified', lastModifiedDate ? <LastModified lastModified={lastModifiedDate} /> : null],
		],
		[type, compassTeamName, lastModifiedDate],
	);
	const Analytics = preparedResultAttributes ? ResultAnalytics : LegacyCompassAnalytics;
	return (
		<Analytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys.Compass}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					url={url}
					icon={<CompassIcon size="medium" />}
					title={enrichedTitle}
					titleMetadata={
						<SearchMetadata
							productConfigs={productConfigs}
							parts={titleMetadataParts}
							displaySpacer={false}
						/>
					}
					headerMetadataParts={headerMetadataParts}
					description={<DescriptionBody description={enrichedDescription} />}
					cloudId={cloudId}
					position={position}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</Analytics>
	);
};

const LegacyCompassAnalytics = ({
	children,
	metadata,
	position,
}: {
	metadata: AdvancedSearchResultInteractedMetadata;
	position: number;
	children: (
		callbacks: Pick<
			ResultAnalyticsCallbacks,
			| 'onResultClicked'
			| 'onResultContextMenu'
			| 'onCopyLinkButtonClicked'
			| 'onSummarizeInChatButtonClicked'
		>,
	) => React.ReactNode;
}): React.JSX.Element => {
	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultContextMenu = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	return (
		<>
			{children({
				onResultClicked,
				onResultContextMenu,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
			})}
		</>
	);
};

export const isSearchCompassComponent = (
	node?: any,
): node is searchCompassComponent_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultCompassComponent';
};

const Tier = ({ tier }: { tier: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Tag
			text={formatMessage(messages.tier, { tier })}
			isRemovable={false}
			migration_fallback="lozenge"
		/>
	);
};
